/**
 * Trades Poster — Pricing Plan (Source Register) workflow helpers.
 */

import {
  BILLING_CYCLE,
  PLAN_NAME_OPTIONS,
  PRICE_CHANGE_NOTICE_DAYS,
  PRICE_HISTORY_STATUS,
  PUBLIC_DESCRIPTION_MAX_LENGTH,
  SAVE_FAILURE_MESSAGE,
  SAVE_SUCCESS_MESSAGE,
  formatGstDisplay,
  formatMonthlyCostFull,
  generatePlanId,
  hasPendingPriceChange,
  resolveCurrentMonthlyCost,
} from './pricingPlanManagementData';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

export {
  SAVE_SUCCESS_MESSAGE,
  SAVE_FAILURE_MESSAGE,
  DISCARD_CONFIRM_TITLE,
  DISCARD_CONFIRM_LABEL,
  DISCARD_CONTINUE_LABEL,
  SAVE_CONFIRM_TITLE,
  SAVE_CONFIRM_LABEL,
  CANCEL_PENDING_TITLE,
  CANCEL_PENDING_CONFIRM_LABEL,
  CANCEL_PENDING_KEEP_LABEL,
} from './pricingPlanManagementData';

const ACTOR = {
  name: 'Saim Sahi',
};

export const VALIDATION_MESSAGES = {
  planNameRequired: 'Select Plan Name.',
  planNameExists: 'A Pricing Plan record already exists for this Plan Name.',
  initialCostRequired: 'Enter Initial Monthly Cost.',
  costGreaterThanZero: 'Monthly Cost must be greater than zero.',
  costDecimals: 'Enter a valid amount using no more than two decimal places.',
  currentCostLocked: 'Current Monthly Cost cannot be edited directly.',
  differentPrice: 'Enter a different price.',
  priceChangePending: 'A price change is already pending.',
  effectiveFromRequired: 'Select Effective From.',
  effectiveFromNotice: 'Effective From must be at least 30 days from today.',
  publicDescriptionRequired: 'Enter Public Description.',
  publicDescriptionMax: 'Public Description must not exceed 200 characters.',
};

function isValidAmountFormat(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  return /^\d+(\.\d{1,2})?$/.test(raw);
}

function hasValidMonthlyCost(value) {
  if (!isValidAmountFormat(value)) return false;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0;
}

function monthlyCostError(value, requiredMessage) {
  if (!String(value ?? '').trim()) return requiredMessage;
  if (!isValidAmountFormat(value)) return VALIDATION_MESSAGES.costDecimals;
  return VALIDATION_MESSAGES.costGreaterThanZero;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getMinimumEffectiveFromDate(fromDate = new Date()) {
  const min = startOfDay(fromDate);
  min.setDate(min.getDate() + PRICE_CHANGE_NOTICE_DAYS);
  return min;
}

export function isEffectiveFromValid(dateStr, fromDate = new Date()) {
  if (!dateStr || !String(dateStr).trim()) return false;
  const selected = startOfDay(dateStr);
  if (Number.isNaN(selected.getTime())) return false;
  return selected >= getMinimumEffectiveFromDate(fromDate);
}

function nextHistoryId(planId, history) {
  const nums = (history || [])
    .map((entry) => {
      const match = new RegExp(`^${planId}-PH-(\\d+)$`, 'i').exec(entry.id || '');
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `${planId}-PH-${String(next).padStart(3, '0')}`;
}

function cloneHistory(history) {
  return (history || []).map((entry) => ({ ...entry }));
}

/**
 * Collect field errors for Save & Confirm.
 * @param {object} formData
 * @param {object[]} allPlans
 * @param {{ excludeId?: string, publishedBaseline?: object|null, isNew?: boolean }} options
 */
export function collectFieldErrors(formData, allPlans, { excludeId, publishedBaseline, isNew = false } = {}) {
  const errors = {};
  const planName = (formData.planName || '').trim();

  if (!planName) {
    errors.planName = VALIDATION_MESSAGES.planNameRequired;
  } else if (!PLAN_NAME_OPTIONS.includes(planName)) {
    errors.planName = VALIDATION_MESSAGES.planNameRequired;
  } else if (publishedBaseline?.planName && planName !== publishedBaseline.planName) {
    errors.planName = VALIDATION_MESSAGES.planNameExists;
  } else if (allPlans.some((plan) => plan.planName === planName && plan.id !== excludeId)) {
    errors.planName = VALIDATION_MESSAGES.planNameExists;
  }

  if (isNew) {
    if (!hasValidMonthlyCost(formData.initialMonthlyCost)) {
      errors.initialMonthlyCost = monthlyCostError(
        formData.initialMonthlyCost,
        VALIDATION_MESSAGES.initialCostRequired,
      );
    }
  } else if (!hasValidMonthlyCost(formData.currentMonthlyCost)) {
    errors.currentMonthlyCost = monthlyCostError(
      formData.currentMonthlyCost,
      'Enter Current Monthly Cost.',
    );
  }

  const description = (formData.publicDescription || '').trim();
  if (!description) {
    errors.publicDescription = VALIDATION_MESSAGES.publicDescriptionRequired;
  } else if (description.length > PUBLIC_DESCRIPTION_MAX_LENGTH) {
    errors.publicDescription = VALIDATION_MESSAGES.publicDescriptionMax;
  }

  if (isNew) return errors;

  const baselineHadPending = hasPendingPriceChange(publishedBaseline);
  const formHasPending = hasPendingPriceChange(formData);
  const formHasEffectiveFrom = Boolean(String(formData.effectiveFrom ?? '').trim());
  const isCancellingPending = baselineHadPending && !formHasPending && !formHasEffectiveFrom;

  // Show schedule fields when scheduling a new change or editing an existing pending change
  const scheduleFieldsVisible =
    Boolean(formData.isSchedulingPriceChange) || formHasPending || formHasEffectiveFrom;

  if (scheduleFieldsVisible && !isCancellingPending) {
    if (!hasValidMonthlyCost(formData.pendingMonthlyCost)) {
      errors.pendingMonthlyCost = monthlyCostError(
        formData.pendingMonthlyCost,
        'Enter New Monthly Cost.',
      );
    } else {
      const current = resolveCurrentMonthlyCost(formData);
      if (Number(formData.pendingMonthlyCost) === Number(current)) {
        errors.pendingMonthlyCost = VALIDATION_MESSAGES.differentPrice;
      }
    }

    if (!formHasEffectiveFrom) {
      errors.effectiveFrom = VALIDATION_MESSAGES.effectiveFromRequired;
    } else if (!isEffectiveFromValid(formData.effectiveFrom)) {
      errors.effectiveFrom = VALIDATION_MESSAGES.effectiveFromNotice;
    }
  }

  return errors;
}

export function getPublishedPlan(plan) {
  if (!plan) return null;
  return {
    ...plan,
    currentMonthlyCost: resolveCurrentMonthlyCost(plan),
    priceHistory: cloneHistory(plan.priceHistory),
  };
}

export function planFormHasChanges(current, baseline, { isNew = false } = {}) {
  if (isNew || !baseline) {
    const empty = !(current.planName || '').trim() &&
      !(current.initialMonthlyCost || '').trim() &&
      !(current.publicDescription || '').trim();
    return !empty;
  }

  const normalize = (plan) => ({
    planName: (plan.planName || '').trim(),
    currentMonthlyCost: String(resolveCurrentMonthlyCost(plan) ?? '').trim(),
    pendingMonthlyCost:
      plan.pendingMonthlyCost == null || plan.pendingMonthlyCost === ''
        ? ''
        : String(Number(plan.pendingMonthlyCost)),
    effectiveFrom: (plan.effectiveFrom || '').trim(),
    publicDescription: (plan.publicDescription || '').trim(),
    gstIncluded: plan.gstIncluded !== false,
    billingCycle: (plan.billingCycle || BILLING_CYCLE).trim(),
    isSchedulingPriceChange: Boolean(plan.isSchedulingPriceChange),
    priceHistory: (plan.priceHistory || []).map((entry) => ({
      id: entry.id,
      previousPrice: entry.previousPrice,
      newPrice: entry.newPrice,
      effectiveFrom: entry.effectiveFrom,
      status: entry.status,
    })),
  });

  return JSON.stringify(normalize(current)) !== JSON.stringify(normalize(baseline));
}

/**
 * Apply cancel-pending to working form state (persisted on Save & Confirm).
 */
export function cancelPendingPriceChangeInForm(formData) {
  const history = cloneHistory(formData.priceHistory);
  const updatedHistory = history.map((entry) =>
    entry.status === PRICE_HISTORY_STATUS.SCHEDULED
      ? { ...entry, status: PRICE_HISTORY_STATUS.CANCELLED }
      : entry,
  );

  return {
    ...formData,
    pendingMonthlyCost: '',
    effectiveFrom: '',
    isSchedulingPriceChange: false,
    priceHistory: updatedHistory,
  };
}

export function mergePlansAfterSave(allPlans, payload, publishedBaseline, { isNew = false } = {}) {
  const nowIso = new Date().toISOString();
  const planName = (payload.planName || '').trim();
  const status = isNew ? 'Active' : (publishedBaseline?.status === 'Inactive' ? 'Inactive' : 'Active');
  const publicDescription = (payload.publicDescription || '').trim();
  const gstIncluded = payload.gstIncluded !== false;
  const billingCycle = (payload.billingCycle || BILLING_CYCLE).trim() || BILLING_CYCLE;

  if (isNew) {
    const planId = generatePlanId(planName);
    const initialCost = Number(payload.initialMonthlyCost);
    const historyEntry = {
      id: nextHistoryId(planId, []),
      previousPrice: null,
      newPrice: initialCost,
      effectiveFrom: nowIso.slice(0, 10),
      status: PRICE_HISTORY_STATUS.APPLIED,
      changedOn: nowIso,
      changedBy: ACTOR.name,
    };

    const record = {
      id: planId,
      planName,
      currentMonthlyCost: initialCost,
      pendingMonthlyCost: null,
      effectiveFrom: null,
      gstIncluded,
      billingCycle,
      publicDescription,
      status,
      priceHistory: [historyEntry],
      addedOn: nowIso,
      addedBy: ACTOR.name,
      lastUpdatedOn: nowIso,
      lastUpdatedBy: ACTOR.name,
    };

    try {
      emitRuleSaveHistory({
        isNew: true,
        previous: null,
        next: record,
        module: 'Pricing Plans',
        sourceModuleRef: 'pricing_plans',
        changedItem: record.planName || planId,
        itemId: planId,
        actor: ACTOR.name,
        stamp: nowIso,
        compareKeys: [
          'planName',
          'currentMonthlyCost',
          'pendingMonthlyCost',
          'effectiveFrom',
          'gstIncluded',
          'billingCycle',
          'publicDescription',
          'status',
        ],
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }

    return [...allPlans, record];
  }

  const planId = publishedBaseline.id;
  let history = cloneHistory(payload.priceHistory || publishedBaseline.priceHistory);
  const baselinePending = hasPendingPriceChange(publishedBaseline);
  const formPending = hasPendingPriceChange(payload);
  const currentCost = Number(resolveCurrentMonthlyCost(payload));

  // Cancel pending: history already marked Cancelled in form
  const cancelledInForm = (payload.priceHistory || []).some(
    (entry) =>
      entry.status === PRICE_HISTORY_STATUS.CANCELLED &&
      (publishedBaseline.priceHistory || []).some(
        (base) => base.id === entry.id && base.status === PRICE_HISTORY_STATUS.SCHEDULED,
      ),
  );

  if (!baselinePending && formPending) {
    // New scheduled change
    history = history.map((entry) =>
      entry.status === PRICE_HISTORY_STATUS.SCHEDULED
        ? { ...entry, status: PRICE_HISTORY_STATUS.CANCELLED }
        : entry,
    );
    history.unshift({
      id: nextHistoryId(planId, history),
      previousPrice: currentCost,
      newPrice: Number(payload.pendingMonthlyCost),
      effectiveFrom: String(payload.effectiveFrom).slice(0, 10),
      status: PRICE_HISTORY_STATUS.SCHEDULED,
      changedOn: nowIso,
      changedBy: ACTOR.name,
    });
  } else if (baselinePending && formPending) {
    // Update existing scheduled entry values
    history = history.map((entry) => {
      if (entry.status !== PRICE_HISTORY_STATUS.SCHEDULED) return entry;
      return {
        ...entry,
        previousPrice: currentCost,
        newPrice: Number(payload.pendingMonthlyCost),
        effectiveFrom: String(payload.effectiveFrom).slice(0, 10),
        changedOn: nowIso,
        changedBy: ACTOR.name,
      };
    });
  } else if (cancelledInForm || (baselinePending && !formPending)) {
    history = cloneHistory(payload.priceHistory || history).map((entry) =>
      entry.status === PRICE_HISTORY_STATUS.SCHEDULED
        ? { ...entry, status: PRICE_HISTORY_STATUS.CANCELLED }
        : entry,
    );
  }

  const record = {
    ...publishedBaseline,
    id: planId,
    planName: publishedBaseline.planName,
    currentMonthlyCost: currentCost,
    pendingMonthlyCost: formPending ? Number(payload.pendingMonthlyCost) : null,
    effectiveFrom: formPending ? String(payload.effectiveFrom).slice(0, 10) : null,
    gstIncluded,
    billingCycle,
    publicDescription,
    status,
    priceHistory: history,
    addedOn: publishedBaseline.addedOn || nowIso,
    addedBy: publishedBaseline.addedBy || ACTOR.name,
    lastUpdatedOn: nowIso,
    lastUpdatedBy: ACTOR.name,
  };

  try {
    emitRuleSaveHistory({
      isNew: false,
      previous: publishedBaseline,
      next: record,
      module: 'Pricing Plans',
      sourceModuleRef: 'pricing_plans',
      changedItem: record.planName || planId,
      itemId: planId,
      actor: ACTOR.name,
      stamp: nowIso,
      compareKeys: [
        'planName',
        'currentMonthlyCost',
        'pendingMonthlyCost',
        'effectiveFrom',
        'gstIncluded',
        'billingCycle',
        'publicDescription',
        'status',
      ],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }

  return allPlans.map((plan) => (plan.id === record.id ? record : plan));
}

export function displayFieldValue(key, value) {
  if (key === 'currentMonthlyCost' || key === 'monthlyCost' || key === 'pendingMonthlyCost') {
    return formatMonthlyCostFull(value);
  }
  if (key === 'gstIncluded') return formatGstDisplay(value);
  if (key === 'status') return value || '—';
  return value == null || value === '' ? '—' : String(value);
}

/** Normalize legacy monthlyCost onto currentMonthlyCost for UI binding. */
export function toFormPlan(plan) {
  if (!plan) return null;
  const current = resolveCurrentMonthlyCost(plan);
  return {
    ...plan,
    currentMonthlyCost: current ?? '',
    initialMonthlyCost: '',
    pendingMonthlyCost:
      plan.pendingMonthlyCost == null || plan.pendingMonthlyCost === ''
        ? ''
        : plan.pendingMonthlyCost,
    effectiveFrom: plan.effectiveFrom || '',
    gstIncluded: plan.gstIncluded !== false,
    billingCycle: plan.billingCycle || BILLING_CYCLE,
    priceHistory: cloneHistory(plan.priceHistory),
    isSchedulingPriceChange: false,
  };
}
